/**
 * useJobEstimate Hook
 *
 * Fetches estimated cost for a job given a provider, model and the node's settings.
 * The API prices per second, per megapixel or per token, so the settings that set those
 * (duration, size, token budget) are sent with the request; everything else is dropped.
 * Uses in-memory caching to avoid redundant API calls for the same model + priced settings.
 *
 * @example
 * ```tsx
 * const { estimate, isLoading, error } = useJobEstimate('fal', 'fal-ai/flux-pro', genieState.settings);
 * // estimate?.priceUsd → "0.05"
 * ```
 */


import { useState, useEffect, useCallback, useMemo } from 'react';
import { apiCall, buildQueryString } from '@/lib/api/api-helpers';
import { useAuth } from '@/hooks/useAuth';
import { pickEstimateSettings } from '@/lib/api/wallet';

export interface JobEstimate {
  priceUsd: string;
  provider: string;
  model: string;
}

interface ApiEstimateResponse {
  success: boolean;
  data: JobEstimate | null;
}

export interface UseJobEstimateResult {
  estimate: JobEstimate | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

// In-memory cache keyed by "provider:model?<priced settings>" (null = no pricing available)
const estimateCache = new Map<string, JobEstimate | null>();
// TTL tracking for null cache entries — avoids caching "no pricing" forever
const nullCacheExpiry = new Map<string, number>();
const NULL_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
// In-flight request dedup — multiple nodes sharing the same model await one fetch
const inflightRequests = new Map<string, Promise<JobEstimate | null>>();

function getCacheKey(provider: string, model: string, pricedQuery: string): string {
  return pricedQuery ? `${provider}:${model}?${pricedQuery}` : `${provider}:${model}`;
}

/**
 * Fetch estimated job cost for a provider+model pair, priced from the node's settings.
 * Returns null estimate when provider or model is not provided.
 */
export function useJobEstimate(
  provider: string | null,
  model: string | null,
  settings?: Record<string, unknown> | null
): UseJobEstimateResult {
  const { isAuthenticated } = useAuth();

  // Serialised so a new settings object with the same priced values does not refetch.
  const pricedQuery = useMemo(
    () => new URLSearchParams(pickEstimateSettings(settings)).toString(),
    [settings]
  );

  const cacheKey = useMemo(
    () => (provider && model ? getCacheKey(provider, model, pricedQuery) : null),
    [provider, model, pricedQuery]
  );

  const [estimate, setEstimate] = useState<JobEstimate | null>(() => {
    if (!cacheKey) return null;
    return estimateCache.get(cacheKey) ?? null;
  });
  const [isLoading, setIsLoading] = useState<boolean>(() => {
    if (!cacheKey) return false;
    return !estimateCache.has(cacheKey);
  });
  const [error, setError] = useState<Error | null>(null);

  const fetchEstimate = useCallback(async () => {
    if (!provider || !model || !cacheKey) {
      setEstimate(null);
      setIsLoading(false);
      return;
    }

    // Return cached value if available (including cached null = no pricing)
    if (estimateCache.has(cacheKey)) {
      const cached = estimateCache.get(cacheKey);
      // If it's a null entry, check if it's expired
      if (cached === null) {
        const expiry = nullCacheExpiry.get(cacheKey);
        if (expiry && Date.now() > expiry) {
          estimateCache.delete(cacheKey);
          nullCacheExpiry.delete(cacheKey);
          // Fall through to re-fetch
        } else {
          setEstimate(null);
          setIsLoading(false);
          return;
        }
      } else {
        setEstimate(cached ?? null);
        setIsLoading(false);
        return;
      }
    }

    // Join an existing in-flight request if one exists for this key
    if (inflightRequests.has(cacheKey)) {
      setIsLoading(true);
      setError(null);
      try {
        const result = await inflightRequests.get(cacheKey)!;
        setEstimate(result ?? null);
      } catch {
        setEstimate(null);
      } finally {
        setIsLoading(false);
      }
      return;
    }

    setIsLoading(true);
    setError(null);

    // Create a single fetch promise that other callers can join
    const fetchPromise = (async (): Promise<JobEstimate | null> => {
      try {
        if (!isAuthenticated) {
          return null;
        }
        const query = buildQueryString({
          provider,
          model,
          ...Object.fromEntries(new URLSearchParams(pricedQuery)),
        });
        const response = await apiCall(`/api/jobs/estimate${query}`, isAuthenticated) as ApiEstimateResponse;
        if (response.success && response.data) {
          estimateCache.set(cacheKey, response.data);
          return response.data;
        }
        return null;
      } catch (err) {
        // 404 / other HTTP errors are thrown by apiCall — cache as null to avoid re-fetching
        const message = err instanceof Error ? err.message : '';
        if (message.includes('HTTP 404')) {
          estimateCache.set(cacheKey, null);
          nullCacheExpiry.set(cacheKey, Date.now() + NULL_CACHE_TTL_MS);
        }
        return null;
      } finally {
        inflightRequests.delete(cacheKey);
      }
    })();

    inflightRequests.set(cacheKey, fetchPromise);

    try {
      const result = await fetchPromise;
      setEstimate(result);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch estimate'));
      setEstimate(null);
    } finally {
      setIsLoading(false);
    }
  }, [provider, model, pricedQuery, cacheKey, isAuthenticated]);

  useEffect(() => {
    fetchEstimate();
  }, [fetchEstimate]);

  return {
    estimate,
    isLoading,
    error,
    refetch: fetchEstimate,
  };
}
